import { useState } from 'react'
import { useApi } from '../../../hooks/useApi'
import { RelocateApiService } from '../services/relocateApi'
import { ValidatePositionResponse, StockBySkuResponse } from '../types'
import { Batch } from '../../../interfaces/Batch'

interface UseOriginValidationProps {
  sharedState: {
    sku: string
    productOriginId: number | null
    hasBatchManagement?: boolean
    hasExpirationDate?: boolean
    posicionOrigen?: string
  }
  setSharedState: (updater: (prev: any) => any) => void
}

interface UseOriginValidationReturn {
  localOriginPosition: string
  setLocalOriginPosition: (value: string) => void
  isValidating: boolean
  originValidated: boolean
  originValidationFailed: boolean
  error: string
  handleValidateOrigin: () => Promise<void>
  handleEditOrigin: () => void
  stockItems: StockBySkuResponse[]
}

export const useOriginValidation = ({
  sharedState,
  setSharedState
}: UseOriginValidationProps): UseOriginValidationReturn => {
  // Inicializar con valores existentes de sharedState (para cuando se vuelve desde ConfirmationStep)
  const [localOriginPosition, setLocalOriginPosition] = useState(sharedState.posicionOrigen || '')
  const [isValidating, setIsValidating] = useState(false)
  const [originValidated, setOriginValidated] = useState(Boolean(sharedState.posicionOrigen))
  const [originValidationFailed, setOriginValidationFailed] = useState(false)
  const [error, setError] = useState('')
  const [stockItems, setStockItems] = useState<StockBySkuResponse[]>([])

  const { callApi: validatePositionCallApi } = useApi<ValidatePositionResponse>()
  const { callApi: stockCallApi } = useApi<StockBySkuResponse[]>()

  const handleValidateOrigin = async () => {
    if (!localOriginPosition.trim()) {
      setError('Ingrese una posición de origen válida')
      return
    }

    setIsValidating(true)
    setError('')
    setOriginValidationFailed(false)

    try {
      let positionTypeId: number | undefined
      let originPositionId: number | undefined
      let originZone: any = null

      // Intentar validar la posición (puede fallar)
      try {
        const response = await validatePositionCallApi(RelocateApiService.validateOriginPosition(
          localOriginPosition.trim(),
          sharedState.sku,
          sharedState.productOriginId!
        ))

        if (response) {
          positionTypeId = response.typePositionId
          originPositionId = response.positionId
          originZone = response.zone
        }
      } catch (validationError) {
        // Continue with stock check
      }

      // Obtener el stock real de esa posición
      const stockResponse = await stockCallApi(RelocateApiService.getStockByPosition(
        localOriginPosition.trim(),
        sharedState.sku,
        sharedState.productOriginId!
      ))

      // Verificar si tenemos datos de stock
      if (!stockResponse || !Array.isArray(stockResponse)) {
        setError('Error al obtener datos de stock del servidor')
        setOriginValidationFailed(true)
        setIsValidating(false)
        return
      }

      // Obtener stock filtrado por posición
      const filteredStockItems = stockResponse.filter((item: StockBySkuResponse) =>
        item.positionCode?.toLowerCase().trim() === localOriginPosition.trim().toLowerCase()
      )

      // Validar que existe stock en la posición
      if (filteredStockItems.length === 0) {
        const availablePositions = stockResponse
          .map(item => item.positionCode)
          .join(', ')
        setError(`No hay stock disponible en esta posición. Posiciones encontradas: ${availablePositions}`)
        setOriginValidationFailed(true)
        setIsValidating(false)
        return
      }

      // Si no obtuvimos datos de validación, usar los datos del primer stock item
      if (!originPositionId && filteredStockItems.length > 0) {
        originPositionId = filteredStockItems[0].positionId
      }

      // Actualizar stockItems para que el hook useBatchSelection lo procese
      setStockItems(filteredStockItems)

      // Calcular stock disponible total
      const availableStock = filteredStockItems.reduce((total, item) => total + (item.quantity || 0), 0)

      // Preparar batches si existen (useBatchSelection determinará si se muestra el modal)
      const realBatches: Batch[] = filteredStockItems
        .filter(item => item.batch != null)
        .map((item) => ({
          batchId: item.batch!.batchId,
          batchNumber: item.batch!.batchNumber || '',
          expirationDate: item.batch!.expirationDate || '',
          quantity: item.quantity
        }))

      // Actualizar estado con todos los datos
      setSharedState(prev => ({
        ...prev,
        posicionOrigen: localOriginPosition.trim(),
        availableStock,
        originPositionId: originPositionId || filteredStockItems[0]?.positionId || 1,
        originPositionType: positionTypeId,
        originZone: originZone,
        stockKeepingUnitId: filteredStockItems[0]?.stockKeepingUnitId,
        availableBatches: realBatches,
        showBatchModal: false  // El componente principal decidirá si mostrar el modal
      }))

      setOriginValidated(true)

      // El hook useBatchSelection y el componente principal manejarán el modal
      // No llamamos onValidationSuccess aquí porque puede que se necesite abrir el modal primero

      // Reset merma state when validating origin
      setSharedState(prev => ({ ...prev, isDamaged: false }))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Posición de origen no válida o sin stock del producto indicado'
      setError(errorMessage)
      setOriginValidated(false)
      setOriginValidationFailed(true)
    } finally {
      setIsValidating(false)
    }
  }

  const handleEditOrigin = () => {
    setOriginValidated(false)
    setOriginValidationFailed(false)
    setSharedState(prev => ({
      ...prev,
      posicionOrigen: '',
      cantidad: null,
      posicionDestino: '',
      availableStock: undefined,
      originPositionId: undefined,
      destinyPositionId: undefined,
      isDamaged: false,
      originPositionType: undefined,
      destinyPositionType: undefined
    }))
    setError('')
  }

  return {
    localOriginPosition,
    setLocalOriginPosition,
    isValidating,
    originValidated,
    originValidationFailed,
    error,
    handleValidateOrigin,
    handleEditOrigin,
    stockItems
  }
}